import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { safeSetItem, safeSetJSON, safeSessionSetItem, loginCleanup } from '@/lib/safeStorage';
import { resealGameData } from '@/lib/gameStats';
import { resealPerfSettings } from '@/lib/performanceSettings';
import { usePublicMode } from '@/contexts/PublicModeContext';
import { KeyPair, generateSigningKeyPair, generateEncryptionKeyPair, generateUID } from '@/lib/crypto';
import { encryptDataBackup, decryptAndRestoreDataBackup, getContacts } from '@/lib/storage';
import {
  storeEncryptedKeys,
  retrieveDecryptedKeys,
  getStoredIdentity,
  hasStoredKeys,
  storeUnlockCode,
  hasStoredUnlockCode,
  getEncryptedUnlockCode,
  StoredIdentity,
  storeLocalVerification,
  isLocalVerificationValid,
  updateLastVerified,
  invalidateLocalVerification,
  shouldSkipServerVerification
} from '@/lib/storage';
import { registerUser, uploadEncryptedKeysBackup, fetchEncryptedKeysBackup, uploadEncryptedDataBackup, fetchEncryptedDataBackup } from '@/lib/api';
import { supabase } from '@/integrations/supabase/client';
import { tryQuickOpenUnlock, purgeQuickOpen, isQuickOpenEnabled, enableQuickOpen, isCheckpointDue, markCheckpointSatisfied, selfHealQuickOpen } from '@/lib/quickOpen';

interface UserKeys {
  signing: KeyPair;
  encryption: KeyPair;
}

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  uid: string | null;
  identity: StoredIdentity | null;
  keys: UserKeys | null;
  pendingUnlockCode: string | null;
  createIdentity: (passphrase: string) => Promise<{ uid: string; recoveryPhrase: string[] }>;
  login: (passphrase: string) => Promise<boolean>;
  recoverIdentity: (uid: string, passphrase: string) => Promise<boolean>;
  logout: () => void;
  hasExistingIdentity: boolean;
  setUnlockCode: (code: string) => void;
  isPublicModeAutoAuth: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const UID_VERIFY_TIMEOUT_MS = 4000;
const QUICK_OPEN_RECOVERY_TIMEOUT_MS = 1500;
const QUICK_OPEN_LOGIN_TIMEOUT_MS = 6500;

async function withTimeout<T>(promise: Promise<T>, timeoutMs: number, fallback: T, label: string): Promise<T> {
  let timeoutId: number | null = null;
  try {
    return await Promise.race([
      promise,
      new Promise<T>((resolve) => {
        timeoutId = window.setTimeout(() => {
          console.warn(`[Auth] ${label} timed out after ${timeoutMs}ms`);
          resolve(fallback);
        }, timeoutMs);
      }),
    ]);
  } catch (error) {
    console.warn(`[Auth] ${label} failed:`, error);
    return fallback;
  } finally {
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
    }
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const { isPublicMode } = usePublicMode();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [uid, setUid] = useState<string | null>(null);
  const [identity, setIdentity] = useState<StoredIdentity | null>(null);
  const [keys, setKeys] = useState<UserKeys | null>(null);
  const [hasExistingIdentity, setHasExistingIdentity] = useState(false);
  const [pendingUnlockCode, setPendingUnlockCode] = useState<string | null>(null);
  const [isPublicModeAutoAuth, setIsPublicModeAutoAuth] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      try {
        const existing = hasStoredKeys();
        if (!isMounted) return;

        setHasExistingIdentity(existing);

        if (!existing) {
          return;
        }

        const storedIdentity = getStoredIdentity();
        setIdentity(storedIdentity);
        setUid(storedIdentity?.uid || null);

        // In Public Mode, mark that we can auto-bypass passphrase
        if (isPublicMode) {
          setIsPublicModeAutoAuth(true);
        }

        // Verify the UID still exists on the server BEFORE leaving loading state.
        // Only wipe identity on a DEFINITIVE "not found" response from the server.
        // Never wipe on transient errors (network, 5xx, timeout).
        // Never wipe paid users (aphenis_unlocked === 'true').
        if (storedIdentity?.uid) {
          try {
            const isPaidUser = localStorage.getItem('aphenis_unlocked') === 'true';
            const { verifyUidExists } = await import('@/lib/api');
            const result = await withTimeout(
              verifyUidExists(storedIdentity.uid, 2),
              UID_VERIFY_TIMEOUT_MS,
              { found: false, transient: true },
              'UID verification'
            );

            if (!isMounted) return;

            if (result.transient) {
              // Network/server issue — never wipe. Allow offline access.
              console.warn('[Auth] UID verification was transient/failed, preserving local identity');
            } else if (!result.found) {
              if (isPaidUser) {
                // Paid users must NEVER lose identity from a server probe.
                console.warn('[Auth] Server says UID missing but user is unlocked (paid). NOT wiping identity.');
              } else {
                console.log('[Auth] Stored UID definitively not found on server, clearing identity keys');
                localStorage.removeItem('vaultmsg_encrypted_keys');
                localStorage.removeItem('vaultmsg_encrypted_unlock_code');
                localStorage.removeItem('vaultmsg_uid');
                // DO NOT remove aphenis_unlocked — payment receipt
                localStorage.removeItem('aphenis_last_verified_code');
                localStorage.removeItem('aphenis_activation_info');
                localStorage.removeItem('aphenis_local_verification');
                localStorage.removeItem('aphenis_fresh_identity');
                localStorage.removeItem('aphenis_onboarding_complete');
                localStorage.removeItem('aphenis_trial_cache');
                setIdentity(null);
                setUid(null);
                setHasExistingIdentity(false);
                setIsAuthenticated(false);
                setIsPublicModeAutoAuth(false);
              }
            }
          } catch (err) {
            // Defensive — never wipe on unexpected exceptions
            console.warn('[Auth] Could not verify UID existence, allowing offline access:', err);
          }
        }

        // Quick-Open auto-unlock — if enabled and healthy, decrypt the
        // device-bound passphrase blob and call login() before exiting the
        // loading state. selfHealQuickOpen() FIRST purges any half-written
        // or repeatedly-failing state, guaranteeing we never deadlock on the
        // shield-icon screen.
        const quickOpenWasHealed = selfHealQuickOpen();
        if (isMounted && storedIdentity?.uid && !quickOpenWasHealed && isQuickOpenEnabled()) {
          try {
            const recoveredPassphrase = await withTimeout(
              tryQuickOpenUnlock(),
              QUICK_OPEN_RECOVERY_TIMEOUT_MS,
              null,
              'Quick-Open recovery'
            );

            if (recoveredPassphrase && isMounted) {
              const ok = await withTimeout(
                login(recoveredPassphrase),
                QUICK_OPEN_LOGIN_TIMEOUT_MS,
                false,
                'Quick-Open auto-login'
              );

              if (ok) {
                console.log('[Auth] Quick-Open auto-unlock successful');
              } else {
                console.warn('[Auth] Quick-Open passphrase recovered but login failed/timed out — falling back to manual unlock');
                // Don't purge — passphrase was correct, login layer just failed.
              }
            }
          } catch (e) {
            console.warn('[Auth] Quick-Open attempt failed silently:', e);
          }
        }
      } catch (error) {
        console.warn('[Auth] initializeAuth failed; falling back to manual unlock:', error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
    };
  }, [isPublicMode]);

  const setUnlockCode = (code: string) => {
    setPendingUnlockCode(code);
  };

  const createIdentity = async (passphrase: string): Promise<{ uid: string; recoveryPhrase: string[] }> => {
    const signingKeys = generateSigningKeyPair();
    const encryptionKeys = generateEncryptionKeyPair();

    const userKeys: UserKeys = {
      signing: signingKeys,
      encryption: encryptionKeys,
    };

    const generatedUid = generateUID(signingKeys.publicKey);

    const result = await registerUser(
      generatedUid,
      signingKeys.publicKey,
      encryptionKeys.publicKey
    );

    if (!result.success) {
      throw new Error(result.error || 'Failed to register');
    }

    // Store encrypted keys with unlock code if available
    await storeEncryptedKeys(userKeys, passphrase, pendingUnlockCode || undefined);

    // Upload encrypted keys backup to server for file-free recovery
    const encryptedKeysBlob = localStorage.getItem('vaultmsg_encrypted_keys');
    const encryptedUnlockCodeBlob = localStorage.getItem('vaultmsg_encrypted_unlock_code');
    if (encryptedKeysBlob) {
      uploadEncryptedKeysBackup(generatedUid, encryptedKeysBlob, encryptedUnlockCodeBlob).catch((err) =>
        console.error('[Auth] Failed to upload keys backup:', err)
      );
    }

    const recoveryPhrase = passphrase.split(' ');

    // Flag this as a fresh identity for first-login onboarding
    safeSetItem('aphenis_fresh_identity', 'true');
    // Cache session passphrase
    safeSessionSetItem('aphenis_session_passphrase', passphrase);

    setKeys(userKeys);
    setUid(generatedUid);
    setIdentity({
      uid: generatedUid,
      signingPublicKey: signingKeys.publicKey,
      encryptionPublicKey: encryptionKeys.publicKey,
    });
    loginCleanup();
    setIsAuthenticated(true);
    setHasExistingIdentity(true);
    setPendingUnlockCode(null);

    return { uid: generatedUid, recoveryPhrase };
  };

  const login = async (passphrase: string): Promise<boolean> => {
    const decryptedData = await retrieveDecryptedKeys(passphrase);

    if (!decryptedData) {
      return false;
    }

    // Verify unlock code is still valid on startup
    if (decryptedData.unlockCode) {
      // Check if local verification is still valid (grace period)
      const hasValidLocalVerification = shouldSkipServerVerification();

      if (hasValidLocalVerification) {
        // Local verification is valid, skip server check but log it
        console.log('[Auth] Local verification valid, skipping server check');
        updateLastVerified();
      } else {
        // Need to verify with server
        try {
          // Race server verification against a 5-second timeout to prevent
          // infinite hang during backend outages
          const verifyPromise = supabase.functions.invoke('check-code-status', {
            body: { unlockCode: decryptedData.unlockCode },
          });
          const timeoutPromise = new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('Server verification timed out')), 5000)
          );
          const { data, error: fnError } = await Promise.race([verifyPromise, timeoutPromise]);

          // Distinguish between server errors and explicit "code revoked" responses.
          // Only invalidate when we get a CLEAN response saying the code is invalid.
          if (fnError || !data) {
            // Server/network error — treat as transient, fall back to local state
            console.warn('[Auth] Server verification returned error, using local fallback:', fnError);
            if (isLocalVerificationValid()) {
              console.log('[Auth] Local verification still valid, allowing login');
            } else {
              const isUnlocked = localStorage.getItem('aphenis_unlocked') === 'true';
              if (!isUnlocked) {
                console.log('[Auth] Cannot verify and no local backup, denying login');
                return false;
              }
              console.log('[Auth] Network error, trusting existing unlock state');
            }
          } else if (!data.valid) {
            // Clean server response explicitly says code is invalid/revoked
            console.log('[Auth] Code revoked remotely, invalidating local verification');
            invalidateLocalVerification();
            return false;
          } else {
            // Server confirmed code is valid — refresh local verification
            storeLocalVerification(decryptedData.unlockCode);
            safeSetItem('aphenis_last_verified_code', decryptedData.unlockCode);
            safeSetItem('aphenis_unlocked', 'true');
            if (data.activationCount !== undefined && data.maxActivations !== undefined) {
              const activationInfo = {
                used: data.activationCount,
                max: data.maxActivations
              };
              try { localStorage.setItem('aphenis_activation_info', JSON.stringify(activationInfo)); } catch {}
            }
          }
        } catch (error) {
          // Timeout or network exception — never invalidate on transient failures
          console.warn('[Auth] Server verification failed/timed out:', error);
          if (isLocalVerificationValid()) {
            console.log('[Auth] Falling back to local verification (still valid)');
          } else {
            const isUnlocked = localStorage.getItem('aphenis_unlocked') === 'true';
            if (!isUnlocked) {
              console.log('[Auth] Cannot verify and no local backup, denying login');
              return false;
            }
            console.log('[Auth] Network error, trusting existing unlock state');
          }
        }
      }
    }

    const storedIdentity = getStoredIdentity();

    setKeys({ signing: decryptedData.signing, encryption: decryptedData.encryption });
    setIdentity(storedIdentity);
    setUid(storedIdentity?.uid || null);
    loginCleanup();
    setIsAuthenticated(true);

    // Ensure aphenis_unlocked is set — it may have been incorrectly removed
    // by a previous version that didn't distinguish server errors from revocations
    safeSetItem('aphenis_unlocked', 'true');

    // Cache session passphrase for in-session backup refreshes
    safeSessionSetItem('aphenis_session_passphrase', passphrase);

    // If Quick-Open is enabled, refresh the encrypted blob so it stays in sync
    // with the current (possibly rotated) passphrase. No-op if disabled.
    if (isQuickOpenEnabled()) {
      // A real unlock satisfies any pending random-interval checkpoint and
      // rolls a fresh deadline (3–60 days). Order matters: clear the pending
      // flag BEFORE refreshing the blob so enableQuickOpen() doesn't reset
      // the schedule on a no-op refresh.
      if (isCheckpointDue()) {
        markCheckpointSatisfied();
      }
      enableQuickOpen(passphrase).catch(() => {});
    }

    // Re-sign integrity seals now that identity keys are available
    resealGameData().catch(() => {});
    resealPerfSettings().catch(() => {});

    // One-time orphan inventory scope sweep (fire-and-forget, 3s timebox)
    if (storedIdentity?.uid) {
      const sweepUid = storedIdentity.uid;
      void (async () => {
        try {
          const { pruneOrphanedInventoryScopes } = await import('@/lib/petInventory');
          await Promise.race([
            pruneOrphanedInventoryScopes(sweepUid),
            new Promise(res => setTimeout(res, 3000)),
          ]);
        } catch {}
      })();
    }

    // Non-critical cloud sync must never block login / bootstrap.
    if (storedIdentity) {
      registerUser(
        storedIdentity.uid,
        storedIdentity.signingPublicKey,
        storedIdentity.encryptionPublicKey
      ).catch((error) => {
        console.warn('[Auth] registerUser sync failed (non-critical):', error);
      });

      const encryptedKeysBlob = localStorage.getItem('vaultmsg_encrypted_keys');
      const encryptedUnlockCodeBlob = localStorage.getItem('vaultmsg_encrypted_unlock_code');
      if (encryptedKeysBlob) {
        uploadEncryptedKeysBackup(storedIdentity.uid, encryptedKeysBlob, encryptedUnlockCodeBlob).catch(() => {});
      }
    }

    if (storedIdentity?.uid && passphrase) {
      void (async () => {
        // Profile picture is in IndexedDB — check async
        const { idbGet } = await import('@/lib/indexedDbStorage');
        const hasPfp = !!(await idbGet('aphenis_profile_picture'));
        const hasAlias = !!localStorage.getItem('aphenis_user_alias');
        const hasNameEffect = !!localStorage.getItem('aphenis_name_effect_config');
        const hasContactProfiles = !!localStorage.getItem('aphenis_contact_profiles');
        const hasContacts = getContacts().length > 0;
        const needsRestore = !hasPfp || !hasAlias || !hasNameEffect || !hasContactProfiles || !hasContacts;

        if (needsRestore) {
          // Missing at least one critical profile domain — eagerly restore from cloud
          try {
            const encryptedDataBlob = await fetchEncryptedDataBackup(storedIdentity.uid);
            if (encryptedDataBlob) {
              const restored = await decryptAndRestoreDataBackup(encryptedDataBlob, passphrase);
              if (restored) {
                console.log('[Auth] Cloud data backup restored on login (missing profile data detected)');
                // Dispatch profile-updated so UI refreshes
                window.dispatchEvent(new Event('profile-updated'));
              }
            }
          } catch (e) {
            console.warn('[Auth] Cloud data restore failed (non-critical):', e);
          }
        } else {
          // Has all local data — do daily auto-backup upload silently
          const lastBackup = localStorage.getItem('aphenis_last_auto_backup');
          const dayMs = 24 * 60 * 60 * 1000;
          const shouldBackup = !lastBackup || (Date.now() - parseInt(lastBackup, 10)) > dayMs;
          if (shouldBackup) {
            encryptDataBackup(passphrase).then(blob => {
              uploadEncryptedDataBackup(storedIdentity.uid, blob).then(() => {
                safeSetItem('aphenis_last_auto_backup', String(Date.now()));
                console.log('[Auth] Daily auto-backup completed');
              }).catch(() => {});
            }).catch(() => {});
          }
        }
      })().catch((error) => {
        console.warn('[Auth] Post-login sync failed (non-critical):', error);
      });
    }

    return true;
  };

  const recoverIdentity = async (importedUid: string, passphrase: string): Promise<boolean> => {
    try {
      // Fetch encrypted keys backup from server
      const backup = await fetchEncryptedKeysBackup(importedUid);
      if (!backup) {
        console.error('[Auth] No encrypted keys backup found on server');
        return false;
      }

      // Store the fetched encrypted data in localStorage so decryption works
      safeSetItem('vaultmsg_encrypted_keys', backup.encryptedKeys);
      if (backup.encryptedUnlockCode) {
        safeSetItem('vaultmsg_encrypted_unlock_code', backup.encryptedUnlockCode);
      }

      // Look up user's public keys from server
      const { lookupUser } = await import('@/lib/api');
      const user = await lookupUser(importedUid);
      if (!user) return false;

      // Store identity info
      const identityInfo: StoredIdentity = {
        uid: importedUid,
        signingPublicKey: user.signing_public_key,
        encryptionPublicKey: user.encryption_public_key,
      };
      safeSetJSON('vaultmsg_uid', identityInfo);

      // Now attempt to decrypt with the passphrase
      const decryptedData = await retrieveDecryptedKeys(passphrase);
      if (!decryptedData) {
        // Clean up if decryption failed
        localStorage.removeItem('vaultmsg_encrypted_keys');
        localStorage.removeItem('vaultmsg_encrypted_unlock_code');
        localStorage.removeItem('vaultmsg_uid');
        return false;
      }

      // If we have an unlock code, verify it's still valid
      if (decryptedData.unlockCode) {
        try {
          const { data } = await supabase.functions.invoke('check-code-status', {
            body: { unlockCode: decryptedData.unlockCode },
          });

          if (!data?.valid) {
            localStorage.removeItem('vaultmsg_encrypted_keys');
            localStorage.removeItem('vaultmsg_encrypted_unlock_code');
            localStorage.removeItem('vaultmsg_uid');
            return false;
          }

          // Increment activation count
          const { data: verifyData } = await supabase.functions.invoke('verify-payment', {
            body: { unlockCode: decryptedData.unlockCode },
          });

          if (!verifyData?.valid) {
            localStorage.removeItem('vaultmsg_encrypted_keys');
            localStorage.removeItem('vaultmsg_encrypted_unlock_code');
            localStorage.removeItem('vaultmsg_uid');
            return false;
          }

          safeSetItem('aphenis_unlocked', 'true');
          if (data.activationCount !== undefined && data.maxActivations !== undefined) {
            safeSetJSON('aphenis_activation_info', {
              used: data.activationCount + 1,
              max: data.maxActivations,
            });
          }

          // Store local verification
          storeLocalVerification(decryptedData.unlockCode);
          safeSetItem('aphenis_last_verified_code', decryptedData.unlockCode);
        } catch (error) {
          console.error('Error verifying unlock code during recovery:', error);
          localStorage.removeItem('vaultmsg_encrypted_keys');
          localStorage.removeItem('vaultmsg_encrypted_unlock_code');
          localStorage.removeItem('vaultmsg_uid');
          return false;
        }
      }

      // Re-register user (ensures public keys are up to date)
      await registerUser(
        identityInfo.uid,
        identityInfo.signingPublicKey,
        identityInfo.encryptionPublicKey
      );

      // Restore contacts + messages from encrypted server backup
      try {
        const encryptedDataBlob = await fetchEncryptedDataBackup(importedUid);
        if (encryptedDataBlob) {
          const restored = await decryptAndRestoreDataBackup(encryptedDataBlob, passphrase);
          if (restored) {
            console.log('[Auth] Contacts and messages restored from backup');
            window.dispatchEvent(new Event('profile-updated'));
          }
        }
      } catch (e) {
        console.warn('[Auth] Data backup restore failed (non-critical):', e);
      }

      // Cache session passphrase for in-session backup refreshes
      safeSessionSetItem('aphenis_session_passphrase', passphrase);

      setKeys({ signing: decryptedData.signing, encryption: decryptedData.encryption });
      setIdentity(identityInfo);
      setUid(identityInfo.uid);
      loginCleanup();
      setIsAuthenticated(true);
      setHasExistingIdentity(true);

      return true;
    } catch {
      return false;
    }
  };

  const logout = useCallback(() => {
    sessionStorage.removeItem('aphenis_session_passphrase');
    // Explicit logout invalidates Quick-Open — user must re-enter passphrase
    // to re-enable it after logging back in.
    purgeQuickOpen();
    setKeys(null);
    setIsAuthenticated(false);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        uid,
        identity,
        keys,
        pendingUnlockCode,
        createIdentity,
        login,
        recoverIdentity,
        logout,
        hasExistingIdentity,
        setUnlockCode,
        isPublicModeAutoAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
